import React, { useEffect, useState, useContext, useMemo } from 'react';
import { UserContext } from '../context/UserContext';
import { API } from '../config';
import styles from './Dashboard.module.css';
import { Link } from 'react-router-dom';
import PlaygroundTiles from './includes/PlaygroundTile';
import TrendingCourses from '../components/includes/TrendingCourses';
import moment from 'moment';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FaMobileAlt, FaCheckCircle, FaCalendarAlt, FaClock, FaRocket,
    FaFire, FaChartPie, FaCalendarTimes, FaVideo, FaChevronLeft, FaChevronRight,
} from 'react-icons/fa';

const localizer = momentLocalizer(moment);

// Cinematic easing — a slow, deliberate deceleration rather than a linear/basic ease.
const CINEMATIC_EASE = [0.16, 1, 0.3, 1];
const MEETINGS_PER_PAGE = 5;

// Animation variants — shared across sections for a consistent, purposeful feel
const fadeUp = {
    hidden: { opacity: 0, y: 22 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: CINEMATIC_EASE } },
};

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08 } },
};

const Dashboard = () => {
    const user = useContext(UserContext);
    const [pages, setPages] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [loadingMeetings, setLoadingMeetings] = useState(true);
    const [meetingsPage, setMeetingsPage] = useState(0);
    const [calendarDate, setCalendarDate] = useState(new Date());
    const [stats, setStats] = useState({
        totalCourses: 0,
        completedCourses: 0,
        upcomingMeetings: 0,
        totalHours: 0
    });

    // Renders a single statistic card
    const StatCard = ({ title, value, icon }) => (
        <motion.div
            className={styles.statCard}
            variants={fadeUp}
            whileHover={{ y: -6, transition: { duration: 0.2, ease: 'easeOut' } }}
            whileTap={{ scale: 0.98 }}
        >
            <div className={styles.statIcon}>{icon}</div>
            <h4>{value}</h4>
            <p>{title}</p>
        </motion.div>
    );

    // Renders a loading state placeholder
    const renderLoadingState = () => (
        <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading your schedule...</p>
        </div>
    );

    // Upcoming meetings first (soonest first), then past meetings (most recent first) —
    // a predictable "what's next, then what happened" order instead of a full calendar.
    const orderedMeetings = useMemo(() => {
        const now = new Date();
        const upcoming = meetings.filter(m => m.end >= now).sort((a, b) => a.start - b.start);
        const past = meetings.filter(m => m.end < now).sort((a, b) => b.start - a.start);
        return [...upcoming, ...past];
    }, [meetings]);

    const totalPages = Math.max(1, Math.ceil(orderedMeetings.length / MEETINGS_PER_PAGE));
    const pagedMeetings = orderedMeetings.slice(
        meetingsPage * MEETINGS_PER_PAGE,
        meetingsPage * MEETINGS_PER_PAGE + MEETINGS_PER_PAGE
    );

    useEffect(() => {
        setMeetingsPage(0);
    }, [meetings]);

    const handleSelectEvent = (event) => {
        const confirmed = window.confirm(
            `Join "${event.title}"?\n\nScheduled: ${moment(event.start).format('MMMM Do YYYY, h:mm A')}\nDuration: ${moment(event.end).diff(moment(event.start), 'minutes')} minutes`
        );
        if (confirmed) {
            window.open(event.joinURL, '_blank');
        }
    };

    const handleNavigate = (newDate) => {
        setCalendarDate(newDate);
    };

    // Renders the meeting list (paginated, 5 at a time) in place of a full calendar
    const renderMeetingList = () => {
        if (orderedMeetings.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <FaCalendarTimes aria-hidden="true" />
                    <h4>No meetings yet</h4>
                    <p>Your schedule is clear. Once a meeting is booked, it'll show up here.</p>
                </div>
            );
        }

        return (
            <>
                <AnimatePresence mode="wait">
                    <motion.ul
                        key={meetingsPage}
                        className={styles.meetingList}
                        variants={staggerContainer}
                        initial="hidden"
                        animate="visible"
                        exit={{ opacity: 0, transition: { duration: 0.15 } }}
                    >
                        {pagedMeetings.map(meeting => (
                            <motion.li
                                key={meeting.id}
                                className={styles.meetingItem}
                                variants={fadeUp}
                                whileHover={{ x: 4, transition: { duration: 0.15 } }}
                            >
                                <div className={styles.meetingDetails}>
                                    <h4 className={styles.meetingTitle}>{meeting.title}</h4>
                                    <p className={styles.meetingTime}>
                                        <FaClock aria-hidden="true" />
                                        {moment(meeting.start).format('MMM Do, h:mm A')} - {moment(meeting.end).format('h:mm A')}
                                    </p>
                                </div>
                                {meeting.end < new Date() ? (
                                    meeting.recordingUrl ? (
                                        <a
                                            href={meeting.recordingUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.recordingButton}
                                        >
                                            <FaVideo aria-hidden="true" /> Watch Recording
                                        </a>
                                    ) : (
                                        <span className={styles.noRecordingLabel}>No recording yet</span>
                                    )
                                ) : (
                                    <a
                                        href={meeting.joinURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.joinButton}
                                    >
                                        Join
                                    </a>
                                )}
                            </motion.li>
                        ))}
                    </motion.ul>
                </AnimatePresence>

                {totalPages > 1 && (
                    <div className={styles.pagination}>
                        <button
                            className={styles.pageBtn}
                            onClick={() => setMeetingsPage(p => Math.max(0, p - 1))}
                            disabled={meetingsPage === 0}
                            aria-label="Previous page"
                        >
                            <FaChevronLeft />
                        </button>
                        <span className={styles.pageIndicator}>
                            Page {meetingsPage + 1} of {totalPages}
                        </span>
                        <button
                            className={styles.pageBtn}
                            onClick={() => setMeetingsPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={meetingsPage >= totalPages - 1}
                            aria-label="Next page"
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                )}
            </>
        );
    };

    useEffect(() => {
        // Existing fetch for pages (only for students)
        const fetchPages = async () => {
            try {
                const res = await fetch(`${API.BASE}/get-dashboard-pages.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ student_id: user.id }),
                });
                const data = await res.json();
                if (data.success) {
                    const parentPages = data.pages.filter(p => p.parent_id === null);
                    setPages(parentPages);

                    // Update stats
                    setStats(prev => ({
                        ...prev,
                        totalCourses: parentPages.length,
                        completedCourses: parentPages.filter(p => p.completed).length
                    }));
                }
            } catch (error) {
                console.error("Failed to fetch pages:", error);
            }
        };

        // Enhanced function to fetch meetings (for ALL users)
        const fetchUserMeetings = async () => {
            // Check to prevent fetching if user or email is not yet available
            if (!user || !user.email) {
                setLoadingMeetings(false);
                return;
            }

            setLoadingMeetings(true);
            try {
                // The backend API will need to determine whether to fetch
                // student-specific meetings or all meetings based on the user's role.
                const response = await fetch(`${API.GET_USER_MEETINGS_ENDPOINT}?email=${user.email}&role=${user.role}`);
                const data = await response.json();

                if (data.success) {
                    const formattedMeetings = data.sessions.map(meeting => ({
                        id: meeting.meeting_key,
                        title: meeting.topic,
                        start: new Date(meeting.start_time),
                        // Assuming meeting.duration_ms is the duration in milliseconds
                        end: new Date(new Date(meeting.start_time).getTime() + parseInt(meeting.duration_ms)),
                        agenda: meeting.agenda,
                        joinURL: meeting.join_link,
                        recordingUrl: meeting.recording_play_url || null
                    }));
                    setMeetings(formattedMeetings);

                    // Calculate upcoming meetings (next 7 days)
                    const now = new Date();
                    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                    const upcomingCount = formattedMeetings.filter(
                        meeting => meeting.start >= now && meeting.start <= nextWeek
                    ).length;

                    // Calculate total hours from meetings
                    const totalMinutes = formattedMeetings.reduce((total, meeting) => {
                        return total + (meeting.end - meeting.start) / (1000 * 60);
                    }, 0);

                    setStats(prev => ({
                        ...prev,
                        upcomingMeetings: upcomingCount,
                        totalHours: Math.round(totalMinutes / 60)
                    }));
                } else {
                    // Handle API failure response
                    setMeetings([]);
                    console.error("API reported failure:", data.message);
                }
            } catch (error) {
                console.error("Failed to fetch user meetings:", error);
                setMeetings([]); // Clear meetings on fetch failure
            } finally {
                setLoadingMeetings(false);
            }
        };

        // --- CORRECTED LOGIC: FETCH MEETINGS FOR ALL LOGGED-IN USERS ---
        if (user) {
            fetchUserMeetings();
        }

        // Only fetch pages if the user is a student
        if (user && user.role === 'student') {
            fetchPages();
        }
        // ----------------------------------------------------------------

    }, [user]);

    return (
        <div className={styles.container}>
            <div className={styles.ambientGlow} aria-hidden="true" />

            <motion.div
                className={styles.header}
                initial={{ opacity: 0, y: -18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: CINEMATIC_EASE }}
            >
                <span className={styles.eyebrow}>Dashboard</span>
                <h1>Welcome back, {user.full_name || user.username}</h1>
                <p>Ready to continue your learning journey? <Link to="/courses">Browse courses</Link> or check your schedule below.</p>
            </motion.div>

            {/* Quick Stats Overview */}
            <motion.div
                className={styles.statsGrid}
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
            >
                <StatCard
                    title="Total Courses"
                    value={stats.totalCourses}
                    icon={<FaMobileAlt aria-hidden="true" />}
                />
                <StatCard
                    title="Completed Courses"
                    value={stats.completedCourses}
                    icon={<FaCheckCircle aria-hidden="true" />}
                />
                <StatCard
                    title="Upcoming Meetings"
                    value={stats.upcomingMeetings}
                    icon={<FaCalendarAlt aria-hidden="true" />}
                />
                <StatCard
                    title="Learning Hours"
                    value={stats.totalHours}
                    icon={<FaClock aria-hidden="true" />}
                />
            </motion.div>

            <div className={styles.dashboardGrid}>
                <div className={styles.mainContent}>
                    <motion.div
                        className={styles.card}
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: CINEMATIC_EASE }}
                    >
                        <h3>
                            <FaCalendarAlt aria-hidden="true" />
                            Your Meetings
                        </h3>
                        {loadingMeetings ? (
                            renderLoadingState()
                        ) : (
                            <>
                                <div className={styles.calendarContainer}>
                                    <Calendar
                                        localizer={localizer}
                                        events={meetings}
                                        date={calendarDate}
                                        onNavigate={handleNavigate}
                                        startAccessor="start"
                                        endAccessor="end"
                                        onSelectEvent={handleSelectEvent}
                                        popup
                                        views={['month', 'week', 'day']}
                                        defaultView="month"
                                        tooltipAccessor={(event) => `${event.title}\n${moment(event.start).format('h:mm A')} - ${moment(event.end).format('h:mm A')}`}
                                    />
                                </div>
                                <h4 className={styles.subheading}>Recent &amp; Upcoming</h4>
                                {renderMeetingList()}
                            </>
                        )}
                    </motion.div>

                    {/* Playground Tiles with enhanced styling */}
                    <motion.div
                        className={styles.card}
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2, ease: CINEMATIC_EASE }}
                    >
                        <h3>
                            <FaRocket aria-hidden="true" />
                            Quick Actions
                        </h3>
                        <PlaygroundTiles />
                    </motion.div>
                </div>

                <aside className={styles.sidebar}>
                    <motion.div
                        className={styles.card}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.15, ease: CINEMATIC_EASE }}
                    >
                        <h3>
                            <FaFire aria-hidden="true" />
                            Trending Courses
                        </h3>
                        <TrendingCourses />
                    </motion.div>

                    {/* Additional sidebar content */}
                    <motion.div
                        className={styles.card}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.25, ease: CINEMATIC_EASE }}
                    >
                        <h3>
                            <FaChartPie aria-hidden="true" />
                            Progress Overview
                        </h3>
                        <div className={styles.progressSection}>
                            <p className={styles.progressLabel}>Course Completion</p>
                            <div className={styles.progressTrack}>
                                <motion.div
                                    className={styles.progressFill}
                                    initial={{ width: 0 }}
                                    animate={{
                                        width: `${stats.totalCourses > 0 ? (stats.completedCourses / stats.totalCourses) * 100 : 0}%`
                                    }}
                                    transition={{ duration: 1, delay: 0.5, ease: CINEMATIC_EASE }}
                                />
                            </div>
                            <p className={styles.progressSummary}>
                                {stats.completedCourses} of {stats.totalCourses} courses completed
                            </p>
                        </div>
                    </motion.div>
                </aside>
            </div>
        </div>
    );
};

export default Dashboard;
